import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  useCurrentUser,
  useDashboardBusinesses,
  useDashboardOverview,
  useSelectDashboardBusiness,
} from "@/api/hooks";
import type { DashboardWorkspace } from "@/api/types";
import { toast } from "@/components/ui/toast";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useSyncProDocumentChrome } from "@/hooks/use-sync-pro-document-chrome";
import { cn } from "@/lib/cn";
import {
  currentDashboardSlug,
  DashboardActiveSlugContext,
  dashboardSectionPath,
} from "@/lib/dashboard-context";
import { extractApiError } from "@/lib/extract-api-error";
import { sessionRevision } from "@/lib/session-request-scope";
import {
  hydrateProThemeFromStorage,
  proThemeClassName,
  useProThemeStore,
} from "@/stores/pro-theme";
import { DashboardBottomNav } from "./DashboardBottomNav";
import { DashboardSidebar } from "./DashboardSidebar";

function DashboardLayout() {
  const theme = useProThemeStore((state) => state.theme);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 768px)");
  const isMobileCanvas = !isTablet;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDashboardSlug, setSelectedDashboardSlug] = useState(() => currentDashboardSlug());
  const routeDashboardSlug = currentDashboardSlug(location.pathname);
  const activeDashboardSlug = routeDashboardSlug ?? selectedDashboardSlug;

  useEffect(() => {
    hydrateProThemeFromStorage();
  }, []);

  useSyncProDocumentChrome(theme, isMobileCanvas);
  const { data: user } = useCurrentUser();
  const { data: overview } = useDashboardOverview(activeDashboardSlug);
  const { data: dashboardBusinesses } = useDashboardBusinesses(activeDashboardSlug);
  const selectDashboardBusiness = useSelectDashboardBusiness();
  const workspaces = dashboardBusinesses?.businesses ?? overview?.businesses ?? [];
  const userName =
    user?.display_name?.trim() ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "Team member";

  const handleWorkspaceSelect = async (workspace: DashboardWorkspace) => {
    let revision = sessionRevision();
    try {
      await selectDashboardBusiness.mutateAsync(workspace.id);
      if (sessionRevision() !== revision + 1) return;
      revision += 1;
      setSelectedDashboardSlug(workspace.slug);
      await navigate({ to: `${dashboardSectionPath(workspace.slug)}${window.location.search}` });
    } catch (error) {
      if (sessionRevision() !== revision) return;
      toast({
        variant: "error",
        title: "Unable to switch workspace",
        description: extractApiError(error, "Please try again."),
      });
    }
  };

  const handleListNewBusiness = () => {
    void navigate({ to: "/onboarding/new" });
  };

  return (
    <DashboardActiveSlugContext.Provider value={activeDashboardSlug}>
      <div
        className={cn(
          proThemeClassName(theme),
          "flex overflow-hidden bg-pro-bg",
          isTablet
            ? "h-[var(--visual-vh,100vh)]"
            : "fixed inset-x-0 top-0 z-0 flex h-[calc(var(--visual-vh,100vh)+var(--visual-offset-top,0px))] max-h-[calc(var(--visual-vh,100vh)+var(--visual-offset-top,0px))] flex-col overflow-hidden pt-[var(--visual-offset-top,0px)]",
        )}
        data-pro-surface={isTablet ? undefined : "mobile"}
      >
        {isTablet && (
          <DashboardSidebar
            collapsed={!isDesktop}
            businessName={overview?.business.name}
            workspaces={workspaces}
            userName={userName}
            userRole={overview?.role}
            switchingWorkspace={selectDashboardBusiness.isPending}
            onWorkspaceSelect={(workspace) => {
              void handleWorkspaceSelect(workspace);
            }}
            onListNewBusiness={handleListNewBusiness}
          />
        )}

        <main
          className={cn(
            "dashboard-main flex min-h-0 flex-1 flex-col overflow-hidden overscroll-y-contain pb-14 md:pb-0",
            isTablet ? "bg-pro-surface" : "bg-pro-bg",
          )}
        >
          <Outlet key={activeDashboardSlug} />
        </main>

        {!isTablet && (
          <DashboardBottomNav
            role={overview?.role}
            businessName={overview?.business.name}
            workspaces={workspaces}
            switchingWorkspace={selectDashboardBusiness.isPending}
            onWorkspaceSelect={(workspace) => {
              void handleWorkspaceSelect(workspace);
            }}
            onListNewBusiness={handleListNewBusiness}
          />
        )}
      </div>
    </DashboardActiveSlugContext.Provider>
  );
}

export { DashboardLayout };
